// Global Search — command-palette style dropdown in the app header.
// Keyboard shortcuts: Ctrl+Shift+A (win/linux) or Cmd+Shift+A (mac).
// Results are grouped by type and navigate directly to the record page.
import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, CornerDownLeft, Loader2 } from "lucide-react";

const TYPE_ICON = {
  sales_orders: "🧾",
  quotations: "💬",
  production_orders: "🏭",
  purchase_orders: "🛒",
  materials: "🧴",
  lots: "🔬",
  customers: "🏢",
  suppliers: "🚚",
  gmp_documents: "📘",
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [groups, setGroups] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const inputRef = React.useRef(null);
  const boxRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  // Global keyboard shortcut — Ctrl/Cmd + Shift + A
  React.useEffect(() => {
    const onKey = (e) => {
      const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click-outside to close
  React.useEffect(() => {
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // iter227 — AbortController + latest-query-wins race protection.
  // A stale response can never overwrite a newer query.  Errors are
  // distinguished from empty results.
  const abortRef = React.useRef(null);
  const reqIdRef = React.useRef(0);
  const [errored, setErrored] = React.useState(false);

  // Debounced search
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setGroups([]); setTotal(0); setErrored(false);
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      return;
    }
    debounceRef.current = setTimeout(async () => {
      // Abort any prior in-flight request and open a new one.
      if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const myId = ++reqIdRef.current;
      setBusy(true); setErrored(false);
      try {
        const r = await api.get("/search", { q }, { signal: ctrl.signal });
        // Latest-query-wins: only apply if we're still the newest request.
        if (myId !== reqIdRef.current) return;
        setGroups(r.groups || []);
        setTotal(r.total || 0);
        setActiveIdx(0);
      } catch (e) {
        if (myId !== reqIdRef.current) return;
        // Aborted requests are silent; only real failures surface.
        if (e?.name === "CanceledError" || e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
        setGroups([]); setTotal(0); setErrored(true);
      } finally {
        if (myId === reqIdRef.current) setBusy(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  // Cancel outstanding request when component unmounts.
  React.useEffect(() => () => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
  }, []);

  const flatItems = React.useMemo(() => {
    const items = [];
    for (const g of groups) for (const it of g.items) items.push(it);
    return items;
  }, [groups]);

  const goto = (item) => {
    if (!item?.route) return;
    setOpen(false); setQ("");
    navigate(item.route);
  };

  const onInputKey = (e) => {
    if (!flatItems.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goto(flatItems[activeIdx]);
    }
  };

  const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
  const shortcutLabel = isMac ? "⌘⇧A" : "Ctrl+⇧+A";

  return (
    <div ref={boxRef} className="flex-1 max-w-lg relative" data-testid="global-search-root">
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-md hover:border-indigo-400 dark:hover:border-indigo-500 text-slate-400 dark:text-slate-500 text-left relative"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <span className="flex-1 truncate">Ara: sipariş, lot, malzeme, INCI, CAS, tedarikçi…</span>
        <kbd className="hidden md:inline text-[10px] font-mono px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400">{shortcutLabel}</kbd>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="global-search-panel">
          <div className="relative border-b border-slate-100 dark:border-slate-700/60">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Aramaya başlayın… (en az 2 karakter)"
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-transparent focus:outline-none dark:text-slate-100"
              data-testid="global-search-input"
              autoFocus
            />
            {busy && <Loader2 className="w-4 h-4 absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"/>}
            <button onClick={() => setOpen(false)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" data-testid="global-search-close">
              <X size={14}/>
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="global-search-results">
            {q.trim().length < 2 && (
              <div className="p-4 text-xs text-slate-400 text-center">Aramaya başlamak için en az 2 karakter girin.</div>
            )}
            {q.trim().length >= 2 && !busy && groups.length === 0 && (
              <div className="p-6 text-xs text-slate-400 text-center" data-testid="global-search-empty">
                <div>"{q}" için sonuç bulunamadı.</div>
                <div className="mt-1 text-[10px]">Yetkisiz modüllerdeki kayıtlar gösterilmez.</div>
              </div>
            )}
            {(() => {
              let i = 0;
              return groups.map((g) => (
                <div key={g.type} data-testid={`gsr-group-${g.type}`}>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 border-y border-slate-100 dark:border-slate-700/60">
                    {TYPE_ICON[g.type] || "•"} {g.label} <span className="text-slate-400 dark:text-slate-500 font-normal">({g.items.length})</span>
                  </div>
                  {g.items.map((it) => {
                    const idx = i++;
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={`${it.type}-${it.id}-${it.code}`}
                        onClick={() => goto(it)}
                        onMouseEnter={() => setActiveIdx(idx)}
                        data-testid={`gsr-item-${it.type}-${it.id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition ${isActive ? "bg-indigo-50 dark:bg-indigo-900/30" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                      >
                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">{it.code || "—"}</span>
                        <span className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{it.title}</div>
                          {it.subtitle && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{it.subtitle}</div>}
                        </span>
                        {isActive && <CornerDownLeft size={12} className="text-indigo-500 shrink-0"/>}
                      </button>
                    );
                  })}
                </div>
              ));
            })()}
          </div>

          {total > 0 && (
            <div className="px-3 py-1.5 text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
              <span>{total} sonuç</span>
              <span className="hidden md:flex items-center gap-2">
                <kbd className="font-mono px-1 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">↑↓</kbd>
                <span>gezin</span>
                <kbd className="font-mono px-1 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">↵</kbd>
                <span>aç</span>
                <kbd className="font-mono px-1 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">esc</kbd>
                <span>kapat</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
